import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';

const NAME_ALLOWED = /[^A-Z0-9ÆØÅ ]/g;
const NUMBER_ALLOWED = /[^0-9]/g;

class JerseyBuilderComponent extends Component {
  /** @type {HTMLElement | null} */
  #productForm = null;

  #state = {
    /** @type {'none' | 'personal' | 'player'} */
    printMode: 'none',
    /** @type {string | null} */
    printVariantId: null,
    /** @type {string | null} */
    badgeVariantId: null,
    /** @type {string | null} */
    giftBoxVariantId: null,
    /** @type {{ name: string, number: string }} */
    personal: { name: '', number: '' },
    /** @type {{ variantId: string, jerseyName: string, number: string, displayName: string, printImageUrl: string } | null} */
    player: null,
  };

  /** @type {(() => void) | null} */
  #variantUpdateHandler = null;

  /** @type {string[]} */
  #digitImageUrls = [];

  /** @type {string[] | null} Trimmed inline-SVG markup per digit, populated by #prepareDigits. */
  #preparedDigits = null;

  /** @type {Promise<string[] | null> | null} */
  #preparingDigits = null;

  connectedCallback() {
    super.connectedCallback();

    this.#productForm = this.#findProductForm();
    if (this.#productForm) {
      this.#productForm.setAttribute('on:submit', `#${this.id}/handleSubmit`);
    }

    const digitsAttr = this.dataset.digitImages || '';
    const parsed = digitsAttr ? digitsAttr.split(',').map((s) => s.trim()).filter(Boolean) : [];
    this.#digitImageUrls = parsed.length === 10 ? parsed : [];

    // Pre-fetch + auto-trim digit SVGs so the renderer has tight-cropped
    // markup ready by the time the user starts typing a number.
    if (this.#digitImageUrls.length === 10) {
      this.#prepareDigits().catch(() => {});
    }

    this.#bindPrintRadios();
    this.#bindBadgeRadios();
    this.#bindGiftBoxRadios();
    this.#bindPersonalInputs();
    this.#bindPlayerCards();
    this.#initFromUrl();

    this.#variantUpdateHandler = () => this.#renderPreview();
    document.addEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#variantUpdateHandler) {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
      this.#variantUpdateHandler = null;
    }
    this.#removeOverlay();
  }

  /**
   * Called by Horizon's declarative event system when the product form submits.
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();

    const error = this.#validate();
    if (error) {
      this.#showError(error);
      return;
    }
    this.#hideError();

    if (!this.#state.printVariantId && !this.#state.badgeVariantId) {
      this.#productForm?.handleSubmit?.(event);
      return;
    }

    this.#submitWithAddons(event);
  }

  #findProductForm() {
    const section = this.closest('.shopify-section') || document;
    return section.querySelector('product-form-component');
  }

  #bindPrintRadios() {
    const radios = this.querySelectorAll('[data-group="print"] .jersey-builder__radio');
    radios.forEach((radio) => {
      radio.addEventListener('click', (event) => {
        event.preventDefault();
        const value = radio.getAttribute('data-value') || 'none';
        const variantId = radio.getAttribute('data-variant-id');
        this.#setSelectedRadio(radios, radio);

        const personal = this.querySelector('[data-print-mode="personal"]');
        const player = this.querySelector('[data-print-mode="player"]');
        if (personal) personal.hidden = value !== 'personal';
        if (player) player.hidden = value !== 'player';

        if (value === 'none') {
          this.#state.printMode = 'none';
          this.#state.printVariantId = null;
          this.#state.personal = { name: '', number: '' };
          this.#state.player = null;
          this.#resetPersonalInputs();
          this.#resetPlayerSelection();
        } else if (value === 'personal') {
          this.#state.printMode = 'personal';
          this.#state.printVariantId = variantId;
          this.#state.player = null;
          this.#resetPlayerSelection();
        } else if (value === 'player') {
          this.#state.printMode = 'player';
          // No generic player variant any more — each player has their own
          // variant. printVariantId is set when a player card is picked.
          this.#state.printVariantId = null;
          this.#state.personal = { name: '', number: '' };
          this.#resetPersonalInputs();
        }

        this.#hideError();
        this.#renderPreview();
      });
    });
  }

  #bindBadgeRadios() {
    const radios = this.querySelectorAll('[data-group="badge"] .jersey-builder__radio');
    radios.forEach((radio) => {
      radio.addEventListener('click', (event) => {
        event.preventDefault();
        const value = radio.getAttribute('data-value') || 'none';
        const variantId = radio.getAttribute('data-variant-id');
        this.#setSelectedRadio(radios, radio);
        this.#state.badgeVariantId = value === 'none' ? null : variantId;
      });
    });
  }

  #bindGiftBoxRadios() {
    const radios = this.querySelectorAll('[data-group="gift-box"] .jersey-builder__radio');
    radios.forEach((radio) => {
      radio.addEventListener('click', (event) => {
        event.preventDefault();
        const value = radio.getAttribute('data-value') || 'none';
        const variantId = radio.getAttribute('data-variant-id');
        this.#setSelectedRadio(radios, radio);
        this.#state.giftBoxVariantId = value === 'none' ? null : variantId;
      });
    });
  }

  #bindPersonalInputs() {
    const nameInput = this.refs.personalNameInput;
    const nameCounter = this.refs.personalNameCounter;
    const numberInput = this.refs.personalNumberInput;
    const numberCounter = this.refs.personalNumberCounter;

    const maxName = Number(this.dataset.maxNameChars) || 14;
    const maxNumber = Number(this.dataset.maxNumberChars) || 2;

    nameInput?.addEventListener('input', () => {
      let value = nameInput.value.toUpperCase().replace(NAME_ALLOWED, '');
      if (value.length > maxName) value = value.slice(0, maxName);
      nameInput.value = value;
      if (nameCounter) nameCounter.textContent = `${value.length}/${maxName}`;
      this.#state.personal.name = value;
      this.#hideError();
      this.#renderPreview();
    });

    numberInput?.addEventListener('input', () => {
      let value = numberInput.value.replace(NUMBER_ALLOWED, '');
      if (value.length > maxNumber) value = value.slice(0, maxNumber);
      numberInput.value = value;
      if (numberCounter) numberCounter.textContent = `${value.length}/${maxNumber}`;
      this.#state.personal.number = value;
      this.#hideError();
      this.#renderPreview();
    });
  }

  #bindPlayerCards() {
    const cards = this.querySelectorAll('.jersey-builder__player-card');
    const dropdown = this.refs.playerDropdown;
    const summary = this.refs.playerSummary;

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        cards.forEach((c) => c.classList.remove('is-selected'));
        card.classList.add('is-selected');

        const variantId = card.getAttribute('data-variant-id') || '';
        const jerseyName = card.getAttribute('data-player-jersey-name') || '';
        const number = card.getAttribute('data-player-number') || '';
        const displayName = card.getAttribute('data-player-name') || jerseyName;
        const printImageUrl = card.getAttribute('data-player-print-image') || '';

        this.#state.player = { variantId, jerseyName, number, displayName, printImageUrl };
        // The picked player's variant id becomes the print line item's variant.
        this.#state.printVariantId = variantId;

        if (summary) summary.textContent = `${displayName} #${number}`;
        if (dropdown) {
          dropdown.classList.add('has-selection');
          dropdown.removeAttribute('open');
        }

        this.#hideError();
        this.#renderPreview();
      });
    });
  }

  #setSelectedRadio(radios, selected) {
    radios.forEach((r) => r.classList.remove('is-selected'));
    selected.classList.add('is-selected');
    const input = selected.querySelector('input[type="radio"]');
    if (input) input.checked = true;
  }

  #resetPersonalInputs() {
    const nameInput = this.refs.personalNameInput;
    const numberInput = this.refs.personalNumberInput;
    const nameCounter = this.refs.personalNameCounter;
    const numberCounter = this.refs.personalNumberCounter;
    const maxName = Number(this.dataset.maxNameChars) || 14;
    const maxNumber = Number(this.dataset.maxNumberChars) || 2;
    if (nameInput) nameInput.value = '';
    if (numberInput) numberInput.value = '';
    if (nameCounter) nameCounter.textContent = `0/${maxName}`;
    if (numberCounter) numberCounter.textContent = `0/${maxNumber}`;
  }

  #resetPlayerSelection() {
    this.querySelectorAll('.jersey-builder__player-card.is-selected').forEach((c) =>
      c.classList.remove('is-selected')
    );
    const dropdown = this.refs.playerDropdown;
    const summary = this.refs.playerSummary;
    if (summary) summary.textContent = '';
    if (dropdown) dropdown.classList.remove('has-selection');
  }

  #findBackImageSlots() {
    const position = Number(this.dataset.backImagePosition) || 2;
    const section = this.closest('.shopify-section') || document;
    const slots = [];

    const carouselSlide = section.querySelector(`.product-media-container-${position}`);
    if (carouselSlide) slots.push(carouselSlide);

    const gridItems = section.querySelectorAll('.media-gallery__grid > li');
    const gridItem = gridItems[position - 1];
    if (gridItem && !slots.includes(gridItem)) slots.push(gridItem);

    return slots;
  }

  #ensureOverlays() {
    const slots = this.#findBackImageSlots();
    return slots.map((slot, index) => {
      let overlay = slot.querySelector(':scope > .jersey-builder-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'jersey-builder-overlay';

        // Curved name: SVG with textPath. Path describes a gentle arc that
        // peaks in the middle ("⌒" shape). Width 100, height 22 in viewBox units.
        const ns = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(ns, 'svg');
        svg.setAttribute('class', 'jersey-builder-overlay__name');
        // viewBox bottom edge sits at the curve baseline (y=26). Cap-heights
        // and deeper-curve peaks render above the viewBox via overflow: visible.
        svg.setAttribute('viewBox', '0 0 100 26');
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        svg.setAttribute('overflow', 'visible');
        svg.setAttribute('aria-hidden', 'true');
        const defs = document.createElementNS(ns, 'defs');
        const path = document.createElementNS(ns, 'path');
        const pathId = `jersey-builder-curve-${this.id}-${index}`;
        path.setAttribute('id', pathId);
        // Curve baseline sits in the lower 60% of viewBox so caps fit above.
        path.setAttribute('d', 'M 0 26 Q 50 4 100 26');
        path.setAttribute('fill', 'none');
        defs.appendChild(path);
        const text = document.createElementNS(ns, 'text');
        text.setAttribute('class', 'jersey-builder-overlay__name-text');
        text.setAttribute('font-size', '14');
        text.setAttribute('fill', 'currentColor');
        const textPath = document.createElementNS(ns, 'textPath');
        textPath.setAttribute('href', `#${pathId}`);
        textPath.setAttribute('startOffset', '50%');
        textPath.setAttribute('text-anchor', 'middle');
        textPath.setAttribute('class', 'jersey-builder-overlay__name-textpath');
        text.appendChild(textPath);
        svg.appendChild(defs);
        svg.appendChild(text);

        const numberEl = document.createElement('span');
        numberEl.className = 'jersey-builder-overlay__number';
        const digitsEl = document.createElement('span');
        digitsEl.className = 'jersey-builder-overlay__digits';
        const imageEl = document.createElement('img');
        imageEl.className = 'jersey-builder-overlay__image';
        imageEl.alt = '';
        overlay.appendChild(svg);
        overlay.appendChild(numberEl);
        overlay.appendChild(digitsEl);
        overlay.appendChild(imageEl);
        slot.appendChild(overlay);
      }
      return overlay;
    });
  }

  #removeOverlay() {
    this.#findBackImageSlots().forEach((slot) => {
      slot.querySelector(':scope > .jersey-builder-overlay')?.remove();
    });
  }

  #renderPreview() {
    const { printMode, personal, player } = this.#state;
    let name = '';
    let number = '';
    let imageUrl = '';

    if (printMode === 'personal') {
      name = personal.name;
      number = personal.number;
    } else if (printMode === 'player' && player) {
      if (player.printImageUrl) {
        imageUrl = player.printImageUrl;
      } else {
        name = player.jerseyName;
        number = player.number;
      }
    }

    if (!name && !number && !imageUrl) {
      this.#removeOverlay();
      return;
    }

    const useDigitImages =
      this.#state.printMode === 'personal' &&
      number.length > 0 &&
      this.#digitImageUrls.length === 10;

    const overlays = this.#ensureOverlays();
    if (overlays.length === 0) return;

    const hostStyle = getComputedStyle(this);
    const nameTop = hostStyle.getPropertyValue('--jersey-builder-name-top').trim();
    const numberTop = hostStyle.getPropertyValue('--jersey-builder-number-top').trim();
    const imageTop = hostStyle.getPropertyValue('--jersey-builder-image-top').trim();
    const imageWidth = hostStyle.getPropertyValue('--jersey-builder-image-width').trim();
    const color = hostStyle.getPropertyValue('--jersey-builder-color').trim();
    const font = hostStyle.getPropertyValue('--jersey-builder-font').trim();

    overlays.forEach((overlay) => {
      if (nameTop) overlay.style.setProperty('--jersey-builder-name-top', nameTop);
      if (numberTop) overlay.style.setProperty('--jersey-builder-number-top', numberTop);
      if (imageTop) overlay.style.setProperty('--jersey-builder-image-top', imageTop);
      if (imageWidth) overlay.style.setProperty('--jersey-builder-image-width', imageWidth);
      if (color) overlay.style.setProperty('--jersey-builder-color', color);
      if (font) overlay.style.setProperty('--jersey-builder-font', font);

      overlay.classList.toggle('is-image', Boolean(imageUrl));
      overlay.classList.toggle('is-digits', useDigitImages);

      const nameSvg = overlay.querySelector('svg.jersey-builder-overlay__name');
      const namePath = overlay.querySelector('svg.jersey-builder-overlay__name path');
      const nameTextPath = overlay.querySelector('.jersey-builder-overlay__name-textpath');

      // Recompute the curve path so the editor's "Name curve depth" slider
      // updates live. Endpoints stay at y=26 (curve dip); we move the Bézier
      // control point. For a quadratic Bézier, the curve's visible peak sits
      // halfway between the control point and the chord midpoint, so the
      // control point Y is offset by 2× the desired visual depth.
      if (namePath) {
        const depthRaw = parseFloat(hostStyle.getPropertyValue('--jersey-builder-name-curve'));
        const depth = Number.isFinite(depthRaw) ? Math.max(0, depthRaw) : 22;
        const controlY = 26 - depth * 2;
        namePath.setAttribute('d', `M 0 26 Q 50 ${controlY} 100 26`);
      }
      const numberEl = overlay.querySelector('.jersey-builder-overlay__number');
      const digitsEl = overlay.querySelector('.jersey-builder-overlay__digits');
      const imageEl = overlay.querySelector('.jersey-builder-overlay__image');

      if (nameSvg) {
        nameSvg.hidden = !name;
      }
      if (nameTextPath) {
        nameTextPath.textContent = name;
        nameTextPath.removeAttribute('textLength');
        nameTextPath.removeAttribute('lengthAdjust');
        if (name) this.#fitNameToPath(nameTextPath);
      }
      if (numberEl) {
        numberEl.textContent = useDigitImages ? '' : number;
      }
      if (digitsEl) {
        if (useDigitImages) {
          this.#renderDigitImages(digitsEl, number);
        } else {
          digitsEl.replaceChildren();
        }
      }
      if (imageEl) {
        if (imageUrl) {
          if (imageEl.getAttribute('src') !== imageUrl) imageEl.setAttribute('src', imageUrl);
        } else {
          imageEl.removeAttribute('src');
        }
      }
    });
  }

  /**
   * Asynchronously fits the curved name to the curve. Most names render at the
   * configured font-size; only names that wouldn't fit get compressed via SVG
   * `textLength` so glyph widths and spacing scale uniformly down.
   *
   * The font-size itself is configurable via the `--jersey-builder-name-size`
   * CSS variable (block setting). Default 9 viewBox units, which works for
   * names up to ~16 characters at standard letter aspect ratios.
   */
  async #fitNameToPath(textPath) {
    if (!textPath.textContent) return;
    const text = textPath.parentElement;
    if (!text) return;

    const fromVar = parseFloat(getComputedStyle(this).getPropertyValue('--jersey-builder-name-size'));
    const fontSize = Number.isFinite(fromVar) && fromVar > 0 ? fromVar : 9;

    textPath.removeAttribute('textLength');
    textPath.removeAttribute('lengthAdjust');
    text.setAttribute('font-size', String(fontSize));

    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch {}
    }
    if (!textPath.isConnected || !textPath.textContent) return;

    const TARGET = 90; // 90% of curve in viewBox units
    const natural = textPath.getComputedTextLength();
    if (natural > TARGET) {
      textPath.setAttribute('textLength', String(TARGET));
      textPath.setAttribute('lengthAdjust', 'spacingAndGlyphs');
    }
  }

  /**
   * Fetches each digit SVG once, runs `getBBox()` on its content to find the
   * actual non-whitespace bounding box, then rewrites the viewBox to match.
   * Returns an array of trimmed SVG markup strings (index = digit value).
   * Falls back to null if any fetch fails — the caller then uses raw <img>.
   */
  async #prepareDigits() {
    if (this.#preparedDigits) return this.#preparedDigits;
    if (this.#preparingDigits) return this.#preparingDigits;
    if (this.#digitImageUrls.length !== 10) return null;

    this.#preparingDigits = (async () => {
      try {
        const responses = await Promise.all(
          this.#digitImageUrls.map((url) =>
            fetch(url).then((r) => (r.ok ? r.text() : null)).catch(() => null)
          )
        );
        if (responses.some((t) => !t)) return null;

        // Hidden, but rendered, sandbox so getBBox() can compute layout.
        const sandbox = document.createElement('div');
        sandbox.style.cssText =
          'position:absolute;left:-9999px;top:0;width:200px;height:200px;pointer-events:none;';
        document.body.appendChild(sandbox);

        // Pass 1: measure each digit's content bbox.
        const measured = responses.map((text) => {
          try {
            sandbox.innerHTML = text || '';
            const svg = /** @type {SVGSVGElement | null} */ (sandbox.querySelector('svg'));
            if (!svg) return null;
            const bbox = svg.getBBox();
            if (!bbox || bbox.width === 0 || bbox.height === 0) return null;
            return { text, bbox };
          } catch {
            return null;
          }
        });

        if (measured.some((m) => !m)) {
          document.body.removeChild(sandbox);
          return null;
        }

        // Establish a SHARED vertical extent across all 10 digits so they
        // render at equal heights with consistent baselines/cap-lines. Each
        // digit only gets its WIDTH trimmed to its own content bounds.
        const commonTop = Math.min(...measured.map((m) => m.bbox.y));
        const commonBottom = Math.max(...measured.map((m) => m.bbox.y + m.bbox.height));
        const commonHeight = commonBottom - commonTop;

        // Pass 2: rewrite each SVG's viewBox using its own x/width but the
        // shared top/height.
        const trimmed = measured.map(({ text, bbox }) => {
          try {
            sandbox.innerHTML = text;
            const svg = /** @type {SVGSVGElement | null} */ (sandbox.querySelector('svg'));
            if (!svg) return text;

            // Tiny outset so anti-aliased stroke edges aren't clipped.
            const outset = Math.min(bbox.width, commonHeight) * 0.02;
            svg.setAttribute(
              'viewBox',
              `${bbox.x - outset} ${commonTop - outset} ${bbox.width + outset * 2} ${commonHeight + outset * 2}`
            );
            svg.removeAttribute('width');
            svg.removeAttribute('height');
            svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

            return svg.outerHTML;
          } catch {
            return text;
          }
        });

        document.body.removeChild(sandbox);
        this.#preparedDigits = trimmed;
        return trimmed;
      } catch (err) {
        console.error('[jersey-builder] failed to prepare digits', err);
        return null;
      } finally {
        this.#preparingDigits = null;
      }
    })();

    return this.#preparingDigits;
  }

  #renderDigitImages(container, numberString) {
    const desired = numberString
      .split('')
      .map((ch) => Number(ch))
      .filter((n) => Number.isInteger(n) && n >= 0 && n <= 9);

    // If the trimmed inline SVGs are ready, render those (auto-cropped).
    // Otherwise render raw <img> first, then upgrade once preparation completes.
    if (this.#preparedDigits) {
      this.#paintDigitsAsInlineSvg(container, desired, this.#preparedDigits);
      return;
    }

    this.#paintDigitsAsImg(container, desired);

    if (this.#preparingDigits) {
      this.#preparingDigits.then((prepared) => {
        if (prepared && container.isConnected) {
          this.#paintDigitsAsInlineSvg(container, desired, prepared);
        }
      });
    }
  }

  #paintDigitsAsInlineSvg(container, desired, prepared) {
    container.replaceChildren();
    desired.forEach((digit) => {
      const wrapper = document.createElement('span');
      wrapper.className = 'jersey-builder-overlay__digit';
      wrapper.innerHTML = prepared[digit];
      container.appendChild(wrapper);
    });
  }

  #paintDigitsAsImg(container, desired) {
    // Replace any inline-SVG wrappers that may exist from a previous render.
    const existing = container.children;
    if (existing.length > 0 && existing[0].tagName !== 'IMG') {
      container.replaceChildren();
    }
    while (container.children.length > desired.length) {
      container.removeChild(container.lastChild);
    }
    desired.forEach((digit, i) => {
      const url = this.#digitImageUrls[digit];
      let img = /** @type {HTMLImageElement | undefined} */ (container.children[i]);
      if (!img) {
        img = document.createElement('img');
        img.alt = String(digit);
        container.appendChild(img);
      }
      if (img.getAttribute('src') !== url) img.setAttribute('src', url);
      img.alt = String(digit);
    });
  }

  #initFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const playerName = params.get('player');
    if (!playerName) return;

    const playerRadio = this.querySelector('.jersey-builder__radio[data-value="player"]');
    if (!playerRadio) return;

    playerRadio.click();

    requestAnimationFrame(() => {
      const target = playerName.toUpperCase();
      const card = this.querySelector(
        `.jersey-builder__player-card[data-player-jersey-name="${CSS.escape(target)}"]`
      );
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.click();
      }

      params.delete('player');
      const search = params.toString();
      const url = window.location.pathname + (search ? `?${search}` : '');
      window.history.replaceState({}, document.title, url);
    });
  }

  #validate() {
    const { printMode, personal, player } = this.#state;
    if (printMode === 'personal') {
      if (!personal.name) return 'Please enter a name for the personal print.';
      if (!personal.number) return 'Please enter a number for the personal print.';
    } else if (printMode === 'player') {
      if (!player) return 'Please select a player.';
    }
    return null;
  }

  #showError(message) {
    const el = this.refs.errorMessage;
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  #hideError() {
    const el = this.refs.errorMessage;
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  #submitWithAddons(event) {
    if (!this.#productForm) return;
    const form = this.#productForm.querySelector('form');
    if (!form) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = Number(formData.get('quantity')) || 1;
    if (!variantId) return;

    const attribution = `jb-${Math.random().toString(36).slice(2, 11)}`;
    const nameProp = this.dataset.nameProperty || 'Name';
    const numberProp = this.dataset.numberProperty || 'Number';

    const items = [
      {
        id: Number(variantId),
        quantity,
        properties: {
          _attribution: attribution,
          _attribution_type: 'main',
        },
      },
    ];

    if (this.#state.printVariantId) {
      const properties = {
        _attribution: attribution,
        _attribution_type: 'addon',
      };
      // For personal print, the customer typed the name/number — carry them
      // as line item properties so warehouse and customer can read them.
      // For player print, the picked player IS the variant — name/number live
      // on the variant's metafields, so no extra properties are needed.
      if (this.#state.printMode === 'personal') {
        properties[nameProp] = this.#state.personal.name;
        properties[numberProp] = this.#state.personal.number;
      }
      items.push({
        id: Number(this.#state.printVariantId),
        quantity,
        properties,
      });
    }

    if (this.#state.badgeVariantId) {
      items.push({
        id: Number(this.#state.badgeVariantId),
        quantity,
        properties: {
          _attribution: attribution,
          _attribution_type: 'addon',
        },
      });
    }

    if (this.#state.giftBoxVariantId) {
      items.push({
        id: Number(this.#state.giftBoxVariantId),
        quantity,
        properties: {
          _attribution: attribution,
          _attribution_type: 'addon',
        },
      });
    }

    const cartItemsSections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.getAttribute('data-section-id'))
      .filter(Boolean);

    const payload = { items };
    if (cartItemsSections.length) payload.sections = cartItemsSections.join(',');

    console.debug('[jersey-builder] adding to cart', payload);

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (r) => {
        const text = await r.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch {
          console.error('[jersey-builder] non-JSON response from cart/add', {
            httpStatus: r.status,
            body: text.slice(0, 500),
          });
          throw new Error(`Cart API returned a non-JSON response (HTTP ${r.status}).`);
        }
        if (!r.ok || json.status) {
          console.error('[jersey-builder] cart/add error', json);
          throw new Error(
            json.description || json.message || `Cart API rejected the request (HTTP ${r.status}).`
          );
        }
        return json;
      })
      .then(async (response) => {
        const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => undefined);

        this.dispatchEvent(
          new CartAddEvent(cart, this.id, {
            source: 'jersey-builder-component',
            itemCount: cart?.item_count ?? quantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((err) => {
        console.error('[jersey-builder] add-to-cart failed', err);
        this.dispatchEvent(
          new CartErrorEvent(this.id, err?.message || 'Cart error', '', {})
        );
        this.#showError(err?.message || 'Could not add to cart. Please try again.');
      });
  }
}

if (!customElements.get('jersey-builder-component')) {
  customElements.define('jersey-builder-component', JerseyBuilderComponent);
}
